/**
 * @module engine3d/solid/prims
 * @summary The extra solid primitives the part modeller needs, on top of the
 *   ones `engine3d/mesh` already builds for the plant models.
 * @description
 *   Two of them, both things a fabricated bulk-solids part cannot be drawn
 *   without:
 *
 *     • `rectLoft` — a rectangular frustum. This is the square hopper, the
 *       chute transition and the tapered bin, none of which a cone can stand in
 *       for: a hopper made on a press brake is four flat plates, and its valley
 *       angles are the whole reason it discharges or hangs up.
 *     • `helix` — a screw flight. A screw feeder drawn without one reads as a
 *       plain pipe, which is exactly the drawing nobody needed.
 *
 *   Both follow `mesh.ts`'s conventions: centred in X and Z, sitting on y = 0,
 *   height along +Y, per-vertex normals.
 *
 *   // INVARIANT: no engine state, no DOM — pure geometry, like mesh.ts.
 */
import { emptyMesh, type Mesh } from '../mesh';
import { TAU, v3, vNorm, type Vec2, type Vec3 } from '../math3';

/** Append a vertex, returning its index. */
function push(m: Mesh, p: Vec3, n: Vec3): number {
  const i = m.pos.length / 3;
  m.pos.push(p.x, p.y, p.z);
  m.nrm.push(n.x, n.y, n.z);
  return i;
}

/** Append a quad as two triangles, skipping either if it is degenerate. */
function quad(m: Mesh, a: Vec3, b: Vec3, c: Vec3, d: Vec3, n: Vec3): void {
  const same = (p: Vec3, q: Vec3) =>
    Math.abs(p.x - q.x) < 1e-5 && Math.abs(p.y - q.y) < 1e-5 && Math.abs(p.z - q.z) < 1e-5;
  const ia = push(m, a, n);
  const ib = push(m, b, n);
  const ic = push(m, c, n);
  const id = push(m, d, n);
  if (!same(a, b) && !same(b, c)) m.idx.push(ia, ib, ic);
  if (!same(a, c) && !same(c, d)) m.idx.push(ia, ic, id);
}

/**
 * A rectangular loft: `w0 × d0` at y = 0 tapering to `w1 × d1` at y = h.
 *
 * Equal ends give a box, a zero top gives a pyramid, and the usual case — a
 * big square top over a small square outlet — is the hopper. The walls get
 * their true tilted normals, so the shading shows the valley angle instead of
 * looking like a straight-sided bin.
 */
export function rectLoft(
  w0: number,
  d0: number,
  w1: number,
  d1: number,
  h: number,
  capBottom = true,
  capTop = true,
): Mesh {
  const m = emptyMesh();
  const a = w0 / 2;
  const b = d0 / 2;
  const c = w1 / 2;
  const e = d1 / 2;
  const hh = Math.max(h, 1e-4);

  // Bottom then top corner, walking the rectangle counter-clockwise seen from
  // above, so the wall winding comes out consistently outward-facing.
  const lo: Vec3[] = [v3(-a, 0, b), v3(a, 0, b), v3(a, 0, -b), v3(-a, 0, -b)];
  const hi: Vec3[] = [v3(-c, h, e), v3(c, h, e), v3(c, h, -e), v3(-c, h, -e)];
  // Outward normal per wall, tilted by that wall's own taper.
  const norms: Vec3[] = [
    vNorm(v3(0, (b - e) / hh, 1)),
    vNorm(v3(1, (a - c) / hh, 0)),
    vNorm(v3(0, -(b - e) / hh, -1)),
    vNorm(v3(-1, -(a - c) / hh, 0)),
  ];
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    quad(m, lo[i], lo[j], hi[j], hi[i], norms[i]);
  }
  if (capBottom && w0 > 1e-4 && d0 > 1e-4)
    quad(m, lo[3], lo[2], lo[1], lo[0], v3(0, -1, 0));
  if (capTop && w1 > 1e-4 && d1 > 1e-4) quad(m, hi[0], hi[1], hi[2], hi[3], v3(0, 1, 0));
  return m;
}

/**
 * A helical flight about the +Y axis — the screw of a screw feeder or auger.
 *
 * Built as a solid ribbon: four vertices per station (inner/outer × bottom/top)
 * swept round and up, so the flight has real plate thickness and shades like
 * metal from both faces. `turns` may be fractional; the flight starts at y = 0.
 *
 * @param rIn   Shaft radius — where the flight is welded on.
 * @param rOut  Outside radius of the flight.
 * @param pitch Rise per full turn.
 * @param turns How many turns to sweep.
 * @param t     Plate thickness.
 */
export function helix(
  rIn: number,
  rOut: number,
  pitch: number,
  turns: number,
  t = 6,
  segPerTurn = 26,
): Mesh {
  const m = emptyMesh();
  const steps = Math.max(3, Math.round(segPerTurn * Math.max(0.1, turns)));
  // Rows of [innerBottom, outerBottom, outerTop, innerTop].
  const rows: number[][] = [];
  const pts: Vec3[][] = [];
  for (let i = 0; i <= steps; i++) {
    const f = i / steps;
    const ang = TAU * turns * f;
    const y = pitch * turns * f;
    const cs = Math.cos(ang);
    const sn = Math.sin(ang);
    // The flight climbs as it turns, so its faces are not flat +Y/-Y: the
    // normal leans back by the helix angle. Without this the screw shades as a
    // stack of washers.
    const lead = pitch / (TAU * Math.max(rOut, 1e-4));
    const up = vNorm(v3(-sn * lead * -1, 1, -cs * lead * -1));
    const dn = v3(-up.x, -up.y, -up.z);
    const rad = v3(cs, 0, sn);
    const p = [
      v3(cs * rIn, y, sn * rIn),
      v3(cs * rOut, y, sn * rOut),
      v3(cs * rOut, y + t, sn * rOut),
      v3(cs * rIn, y + t, sn * rIn),
    ];
    pts.push(p);
    rows.push([
      push(m, p[0], dn),
      push(m, p[1], dn),
      push(m, p[2], up),
      push(m, p[3], up),
      push(m, p[1], rad),
      push(m, p[2], rad),
      push(m, p[0], v3(-rad.x, 0, -rad.z)),
      push(m, p[3], v3(-rad.x, 0, -rad.z)),
    ]);
  }
  for (let i = 0; i < steps; i++) {
    const a = rows[i];
    const b = rows[i + 1];
    // Underside, top face, outer rim, inner rim.
    m.idx.push(a[0], b[0], b[1], a[0], b[1], a[1]);
    m.idx.push(a[3], a[2], b[2], a[3], b[2], b[3]);
    m.idx.push(a[4], b[4], b[5], a[4], b[5], a[5]);
    m.idx.push(a[7], a[6], b[6], a[7], b[6], b[7]);
  }
  // End caps, so a flight cut mid-turn is not hollow.
  const capAt = (k: number, flip: boolean) => {
    const p = pts[k];
    const n = flip ? v3(0, -1, 0) : v3(0, 1, 0);
    if (flip) quad(m, p[3], p[2], p[1], p[0], n);
    else quad(m, p[0], p[1], p[2], p[3], n);
  };
  capAt(0, true);
  capAt(steps, false);
  return m;
}

/** How a vessel is closed off at one end. */
export type VesselEnd = 0 | 1 | 2;

/** How far an end of the given type stands off the shell. */
function endRise(r: number, end: VesselEnd, coneH: number): number {
  // A 2:1 semi-ellipsoidal head is half the radius deep — the standard dished
  // end on a pressure vessel. A cone uses the height the user asked for.
  return end === 1 ? r / 2 : end === 2 ? coneH || r * 0.8 : 0;
}

/**
 * The profile of a cylindrical vessel, bottom to top, ready for `revolve`.
 *
 * The two ends are chosen INDEPENDENTLY — a silo is a cone under a shell under
 * a dished roof, and a vessel builder that forces both ends to match cannot
 * draw one. Ends: 0 flat, 1 dished (2:1 semi-ellipsoidal), 2 conical.
 *
 * Total height is `rise(bottom) + h + rise(top)`, so the height the panel
 * reports is the height the parameters describe.
 */
export function vesselProfile(
  r: number,
  h: number,
  endBot: VesselEnd,
  endTop: VesselEnd = 0,
  coneH = 0,
): Vec2[] {
  const pts: Vec2[] = [];
  const rings = 7;
  const b = endRise(r, endBot, coneH);
  const t = endRise(r, endTop, coneH);

  // ── Bottom end: from the axis out to the shell ──
  if (endBot === 1) {
    for (let i = 0; i <= rings; i++) {
      const a = (Math.PI / 2) * (i / rings);
      pts.push({ x: r * Math.sin(a), y: b - b * Math.cos(a) });
    }
  } else {
    // Flat closes at y = 0; conical rises to the shell over `b`.
    pts.push({ x: 0, y: 0 }, { x: r, y: b });
  }

  // ── Shell ──
  pts.push({ x: r, y: b + h });

  // ── Top end: from the shell back in to the axis ──
  if (endTop === 1) {
    for (let i = 1; i <= rings; i++) {
      const a = (Math.PI / 2) * (i / rings);
      pts.push({ x: r * Math.cos(a), y: b + h + t * Math.sin(a) });
    }
  } else {
    pts.push({ x: 0, y: b + h + t });
  }
  return pts;
}
